import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../../context/AuthContext';
import axios from 'axios';
import { 
  Users, Search, Shield, Ban, CheckCircle, Trash2, Plus, 
  ChevronLeft, ChevronRight, Edit2, X, Save, Eye, EyeOff,
  Key, Smartphone, ShieldCheck, ShieldOff, MessageSquareOff, MessageSquare
} from 'lucide-react';

const ROLES = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-chakra-crown' },
  { value: 'user_admin', label: 'User Admin', color: 'bg-chakra-third' },
  { value: 'content_admin', label: 'Content Admin', color: 'bg-chakra-throat' },
  { value: 'practitioner_admin', label: 'Practitioner Admin', color: 'bg-chakra-heart' },
  { value: 'practitioner', label: 'Practitioner', color: 'bg-cosmic-turquoise' },
  { value: 'user', label: 'Member', color: 'bg-cosmic-purple' },
];

const STATUSES = [
  { value: 'active', label: 'Active', color: 'text-chakra-heart' },
  { value: 'blocked', label: 'Blocked', color: 'text-red-400' },
  { value: 'pending', label: 'Pending', color: 'text-cosmic-gold' },
];

export default function AdminUsers() {
  const { token, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ skip: 0, limit: 10, total: 0 });
  
  const [editingUser, setEditingUser] = useState(null);
  const [blockingUser, setBlockingUser] = useState(null);
  const [blockMessage, setBlockMessage] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'user' });
  const [showPassword, setShowPassword] = useState(false);
  
  // MFA and Password Reset state
  const [resettingPasswordFor, setResettingPasswordFor] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [mfaActionLoading, setMfaActionLoading] = useState(null);

  useEffect(() => {
    loadUsers();
  }, [search, roleFilter, statusFilter, pagination.skip]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: pagination.skip,
        limit: pagination.limit,
      });
      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(
        `${API}/admin/users/${userId}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadUsers();
      setEditingUser(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleBlockUser = async () => {
    if (!blockingUser) return;
    try {
      await axios.post(
        `${API}/admin/users/${blockingUser.id}/block`,
        { blocked_message: blockMessage || 'Your account has been suspended.' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadUsers();
      setBlockingUser(null);
      setBlockMessage('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to block user');
    }
  };

  const handleUnblockUser = async (userId) => {
    try {
      await axios.post(
        `${API}/admin/users/${userId}/unblock`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to unblock user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/users/create?role=${newUser.role}`,
        { email: newUser.email, password: newUser.password, name: newUser.name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadUsers();
      setCreatingUser(false);
      setNewUser({ email: '', password: '', name: '', role: 'user' });
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleToggleMFA = async (userId, currentMfaStatus) => {
    setMfaActionLoading(userId);
    try {
      await axios.post(
        `${API}/admin/users/${userId}/toggle-mfa`,
        { enable: !currentMfaStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to toggle MFA');
    } finally {
      setMfaActionLoading(null);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resettingPasswordFor || !newPassword) return;
    
    try {
      await axios.post(
        `${API}/admin/users/${resettingPasswordFor.id}/reset-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Password reset successfully');
      setResettingPasswordFor(null);
      setNewPassword('');
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleToggleCommunityChat = async (userId, currentStatus) => {
    try {
      const response = await axios.post(
        `${API}/admin/users/${userId}/toggle-community-chat`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(response.data.message);
      loadUsers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to toggle community chat access');
    }
  };

  const getRoleBadge = (role) => {
    const r = ROLES.find(r => r.value === role);
    return r || ROLES[5];
  };

  const isSuperAdmin = currentUser?.role === 'super_admin';

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="glass-card p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-cosmic-gold" />
            <h1 className="text-2xl font-cinzel gold-text">User Management</h1>
          </div>
          <button
            onClick={() => setCreatingUser(true)}
            className="cosmic-btn flex items-center gap-2 text-sm"
            data-testid="create-user-btn"
          >
            <Plus className="w-4 h-4" />
            Create User
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-cosmic-gold/50" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="cosmic-input w-full pl-10"
              data-testid="search-input"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="cosmic-input"
            data-testid="role-filter"
          >
            <option value="" className="bg-cosmic-deepSpace">All Roles</option>
            {ROLES.map(r => (
              <option key={r.value} value={r.value} className="bg-cosmic-deepSpace">{r.label}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="cosmic-input"
            data-testid="status-filter"
          >
            <option value="" className="bg-cosmic-deepSpace">All Status</option>
            {STATUSES.map(s => (
              <option key={s.value} value={s.value} className="bg-cosmic-deepSpace">{s.label}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-cosmic-gold/30 border-t-cosmic-gold rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-cosmic-gold/20">
                    <th className="text-left py-3 px-4 text-cosmic-goldLight font-cinzel text-sm">User</th>
                    <th className="text-left py-3 px-4 text-cosmic-goldLight font-cinzel text-sm">Role</th>
                    <th className="text-left py-3 px-4 text-cosmic-goldLight font-cinzel text-sm">Status</th>
                    <th className="text-left py-3 px-4 text-cosmic-goldLight font-cinzel text-sm">MFA</th>
                    <th className="text-left py-3 px-4 text-cosmic-goldLight font-cinzel text-sm">Joined</th>
                    <th className="text-right py-3 px-4 text-cosmic-goldLight font-cinzel text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const roleBadge = getRoleBadge(u.role);
                    const statusInfo = STATUSES.find(s => s.value === u.status) || STATUSES[0];
                    const isEditing = editingUser?.id === u.id;
                    
                    return (
                      <tr key={u.id} className="border-b border-cosmic-gold/10 hover:bg-cosmic-gold/5">
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-cosmic-starWhite font-cormorant">{u.name}</p>
                            <p className="text-cosmic-starWhite/50 text-sm">{u.email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          {isEditing ? (
                            <select
                              value={editingUser.role}
                              onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                              className="cosmic-input py-1 px-2 text-sm"
                            >
                              {ROLES.map(r => (
                                <option key={r.value} value={r.value} className="bg-cosmic-deepSpace">
                                  {r.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className={`text-xs px-2 py-1 rounded ${roleBadge.color} text-white`}>
                              {roleBadge.label}
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <span className={`${statusInfo.color} text-sm`}>{statusInfo.label}</span>
                          {u.blocked_message && (
                            <p className="text-cosmic-starWhite/40 text-xs mt-1 max-w-[200px] truncate">
                              {u.blocked_message}
                            </p>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <button
                            onClick={() => handleToggleMFA(u.id, u.mfa_enabled)}
                            disabled={mfaActionLoading === u.id || (u.role === 'super_admin' && !isSuperAdmin)}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs transition-all ${
                              u.mfa_enabled 
                                ? 'bg-chakra-heart/20 text-chakra-heart hover:bg-chakra-heart/30' 
                                : 'bg-cosmic-gold/10 text-cosmic-starWhite/50 hover:bg-cosmic-gold/20'
                            } disabled:opacity-50`}
                            title={u.mfa_enabled ? 'Click to disable MFA' : 'Click to enable MFA'}
                            data-testid={`toggle-mfa-${u.id}`}
                          >
                            {mfaActionLoading === u.id ? (
                              <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                            ) : u.mfa_enabled ? (
                              <ShieldCheck className="w-3 h-3" />
                            ) : (
                              <ShieldOff className="w-3 h-3" />
                            )}
                            <span>{u.mfa_enabled ? 'Enabled' : 'Disabled'}</span>
                          </button>
                        </td>
                        <td className="py-4 px-4 text-cosmic-starWhite/60 text-sm">
                          {new Date(u.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex items-center justify-end gap-2">
                            {isEditing ? (
                              <>
                                <button
                                  onClick={() => handleUpdateRole(u.id, editingUser.role)}
                                  className="p-2 rounded-lg bg-chakra-heart/20 text-chakra-heart hover:bg-chakra-heart/30"
                                  title="Save"
                                >
                                  <Save className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setEditingUser(null)}
                                  className="p-2 rounded-lg bg-cosmic-gold/10 text-cosmic-starWhite/60 hover:bg-cosmic-gold/20"
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => setEditingUser({ id: u.id, role: u.role })}
                                  className="p-2 rounded-lg bg-cosmic-gold/10 text-cosmic-goldLight hover:bg-cosmic-gold/20"
                                  title="Edit Role"
                                  data-testid={`edit-user-${u.id}`}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                
                                <button
                                  onClick={() => setResettingPasswordFor(u)}
                                  className="p-2 rounded-lg bg-chakra-throat/10 text-chakra-throat hover:bg-chakra-throat/20"
                                  title="Reset Password"
                                  disabled={u.role === 'super_admin' && !isSuperAdmin}
                                  data-testid={`reset-password-${u.id}`}
                                >
                                  <Key className="w-4 h-4" />
                                </button>
                                
                                {u.status === 'active' ? (
                                  <button
                                    onClick={() => setBlockingUser(u)}
                                    className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20"
                                    title="Block User"
                                    disabled={u.role === 'super_admin'}
                                    data-testid={`block-user-${u.id}`}
                                  >
                                    <Ban className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => handleUnblockUser(u.id)}
                                    className="p-2 rounded-lg bg-chakra-heart/10 text-chakra-heart hover:bg-chakra-heart/20"
                                    title="Unblock User"
                                    data-testid={`unblock-user-${u.id}`}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                )}
                                
                                {/* Community Chat Toggle */}
                                {u.role !== 'super_admin' && (
                                  <button
                                    onClick={() => handleToggleCommunityChat(u.id, u.community_chat_disabled)}
                                    className={`p-2 rounded-lg ${
                                      u.community_chat_disabled 
                                        ? 'bg-orange-500/10 text-orange-400 hover:bg-orange-500/20'
                                        : 'bg-chakra-heart/10 text-chakra-heart hover:bg-chakra-heart/20'
                                    }`}
                                    title={u.community_chat_disabled ? "Enable Community Chat" : "Disable Community Chat"}
                                    data-testid={`toggle-chat-${u.id}`}
                                  >
                                    {u.community_chat_disabled ? (
                                      <MessageSquareOff className="w-4 h-4" />
                                    ) : (
                                      <MessageSquare className="w-4 h-4" />
                                    )}
                                  </button>
                                )}
                                
                                {isSuperAdmin && u.role !== 'super_admin' && (
                                  <button
                                    onClick={() => handleDeleteUser(u.id)}
                                    className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20"
                                    title="Delete User"
                                    data-testid={`delete-user-${u.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-cosmic-gold/20">
              <p className="text-cosmic-starWhite/60 text-sm">
                Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPagination(p => ({ ...p, skip: Math.max(0, p.skip - p.limit) }))}
                  disabled={pagination.skip === 0}
                  className="p-2 rounded-lg bg-cosmic-gold/10 text-cosmic-goldLight hover:bg-cosmic-gold/20 disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPagination(p => ({ ...p, skip: p.skip + p.limit }))}
                  disabled={pagination.skip + pagination.limit >= pagination.total}
                  className="p-2 rounded-lg bg-cosmic-gold/10 text-cosmic-goldLight hover:bg-cosmic-gold/20 disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {blockingUser && (
        <div className="fixed inset-0 bg-cosmic-deepSpace/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 max-w-md w-full">
            <h3 className="text-xl font-cinzel text-cosmic-goldLight mb-4">Block User</h3>
            <p className="text-cosmic-starWhite/70 mb-4">
              Block <strong>{blockingUser.name}</strong>? They will not be able to log in.
            </p>
            <textarea
              value={blockMessage}
              onChange={(e) => setBlockMessage(e.target.value)}
              placeholder="Reason for blocking (visible to user)..."
              className="cosmic-input w-full h-24 resize-none mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setBlockingUser(null); setBlockMessage(''); }}
                className="px-4 py-2 rounded-lg border border-cosmic-gold/30 text-cosmic-starWhite/70 hover:bg-cosmic-gold/10"
              >
                Cancel
              </button>
              <button
                onClick={handleBlockUser}
                className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
              >
                Block User
              </button>
            </div>
          </div>
        </div>
      )}

      {creatingUser && (
        <div className="fixed inset-0 bg-cosmic-deepSpace/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 max-w-md w-full">
            <h3 className="text-xl font-cinzel text-cosmic-goldLight mb-4">Create New User</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <input
                type="text"
                placeholder="Full Name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="cosmic-input w-full"
                required
              />
              <input
                type="email"
                placeholder="Email Address"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="cosmic-input w-full"
                required
              />
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="cosmic-input w-full pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-cosmic-gold/60"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="cosmic-input w-full"
              >
                {ROLES.filter(r => isSuperAdmin || r.value !== 'super_admin').map(r => (
                  <option key={r.value} value={r.value} className="bg-cosmic-deepSpace">{r.label}</option>
                ))}
              </select>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setCreatingUser(false)}
                  className="px-4 py-2 rounded-lg border border-cosmic-gold/30 text-cosmic-starWhite/70 hover:bg-cosmic-gold/10"
                >
                  Cancel
                </button>
                <button type="submit" className="cosmic-btn">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resettingPasswordFor && (
        <div className="fixed inset-0 bg-cosmic-deepSpace/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="glass-card p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <Key className="w-6 h-6 text-chakra-throat" />
              <h3 className="text-xl font-cinzel text-cosmic-goldLight">Reset Password</h3>
            </div>
            <p className="text-cosmic-starWhite/70 mb-4">
              Set a new password for <strong>{resettingPasswordFor.name}</strong> ({resettingPasswordFor.email})
            </p>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="cosmic-input w-full pr-10"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-cosmic-gold/60"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-cosmic-starWhite/50 text-xs">
                Password must be at least 8 characters with uppercase, lowercase, number, and special character.
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setResettingPasswordFor(null); setNewPassword(''); setShowNewPassword(false); }}
                  className="px-4 py-2 rounded-lg border border-cosmic-gold/30 text-cosmic-starWhite/70 hover:bg-cosmic-gold/10"
                >
                  Cancel
                </button>
                <button type="submit" className="cosmic-btn">
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
